/*
 * sts-article.js — the four jobs the v4 article page cannot do in Liquid.
 * Workstream 07 (editorial). Spec 07 §6.4 (what is client-side), §6.5, §9.
 *
 * Everything else on this page is server-rendered. The call to action picks its
 * state from CSS, the breadcrumbs, schema, table of contents, related reading
 * and every product card arrive in the HTML, and the page is complete with
 * JavaScript switched off. What is left is only what the server genuinely
 * cannot know or cannot do:
 *
 *   1. Open the table of contents on a wide screen. It ships closed because at
 *      390px an open list of eight links pushes the first line of body text off
 *      the first screen (criterion 19), and CSS cannot open a <details>.
 *
 *   2. Resolve the product fit verdict for a signed-OUT reader who has a type.
 *      snippets/sts-product-fit.liquid returns "unknown" for them — the server
 *      has no customer record to read — but it emits the raw approval data on
 *      every card. The type itself is already on <html> before first paint,
 *      resolved by workstream 02. So the same rules run here over the same
 *      data, and the answer is identical to the one a signed-in reader gets.
 *      This is why an article can never contradict the reader's routine.
 *
 *   3. Copy the article link.
 *
 *   4. Load Swiper, then assets/article.js, in that order, and only when the
 *      article actually contains a product shortcode. article.js constructs
 *      `new Swiper(...)` inside renderSliders(), so loading both with `defer`
 *      leaves the order to chance; losing that race throws inside the render
 *      loop and takes every slider on the page with it. Swiper's URL arrives as
 *      a data attribute rather than a <script src> so the theme carries no
 *      remote asset tag.
 *
 * Analytics: only the call to action's quiz taps (spec 09 §6.9 events 2, 5,
 * 6), and only through window.stsTrack, whose ad gate keeps them away from
 * every pixel (decision 09-Q11). Nothing here sends a skin type, a concern or
 * a condition-shaped topic, and the article itself is not named in a payload.
 */
(function () {
  'use strict';

  var SCRIPT_ID = 'sts-article-js';
  var DESKTOP = '(min-width: 861px)';
  var SUBTYPES = ['S1', 'S2', 'S3', 'S4'];
  var SUBTYPE_NAMES = ['acne', 'redness', 'stinging', 'allergic'];

  function attr(name) {
    var tag = document.getElementById(SCRIPT_ID);
    return tag ? tag.getAttribute(name) : null;
  }

  function read(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  // ------------------------------------------------- 1. table of contents

  function openTableOfContents() {
    var toc = document.querySelector('[data-sts-article-toc]');
    if (!toc || !window.matchMedia) return;
    if (window.matchMedia(DESKTOP).matches) toc.open = true;
  }

  // -------------------------------------------------- 2. the fit verdict

  /* The customer's type, as workstream 02 already resolved it. Falls back to
     the raw key only if the resolver has not run, which should not happen —
     it is synchronous and in <head>. */
  function readerType() {
    var state = window.STS && window.STS.state;
    if (state && state.type) return String(state.type).toUpperCase();
    var raw = read('customerSkinType');
    return raw ? String(raw).trim().toUpperCase() : '';
  }

  /* The sensitivity subtypes the reader needs, normalised to S1-S4. The store
     writes this list in two vocabularies depending on which code wrote it
     ("S1" and "Acne"), exactly as snippets/sts-product-fit.liquid documents, so
     both are accepted here too. R1 means resistant — no subtype constraint at
     all — and is deliberately not a subtype. */
  function readerSubtypes() {
    var raw = read('customerSensitivityList');
    if (!raw) return [];

    var list;
    try {
      list = JSON.parse(raw);
    } catch (e) {
      list = String(raw).split(',');
    }
    if (!Array.isArray(list)) list = [list];

    var out = [];
    for (var i = 0; i < list.length; i++) {
      var value = String(list[i] || '').trim();
      if (!value) continue;
      var code = value.toUpperCase();
      if (SUBTYPES.indexOf(code) === -1) {
        var named = SUBTYPE_NAMES.indexOf(value.toLowerCase());
        code = named === -1 ? '' : SUBTYPES[named];
      }
      if (code && out.indexOf(code) === -1) out.push(code);
    }
    return out;
  }

  /* The same four-way decision snippets/sts-product-fit.liquid makes, over the
     same attributes it emitted. `unrated` is not `barred`: a product with no
     Baumann data at all — a gift card, a device, a supplement — must never be
     told to anyone as "not for your type". */
  function verdict(card, type, subtypes) {
    var count = parseInt(card.getAttribute('data-sts-approved-count'), 10) || 0;
    if (count === 0) return 'unrated';
    if (!type) return 'unknown';

    var approved = ' ' + (card.getAttribute('data-sts-approved') || '') + ' ';
    if (approved.indexOf(' ' + type + ' ') === -1) return 'barred';

    var carried = ' ' + (card.getAttribute('data-sts-sens') || '') + ' ';
    for (var i = 0; i < subtypes.length; i++) {
      if (carried.indexOf(' ' + subtypes[i] + ' ') === -1) return 'subtype';
    }
    return 'approved';
  }

  function applyFit() {
    var type = readerType();
    if (!type) return;

    var subtypes = readerSubtypes();
    var cards = document.querySelectorAll('[data-sts-approved-count]');
    for (var i = 0; i < cards.length; i++) {
      cards[i].setAttribute('data-sts-fit', verdict(cards[i], type, subtypes));
    }

    /* Inside the "Products for skin type" block the verdict is a filter, not a
       label: the block promises products for this reader, so anything barred or
       missing a subtype they need is removed rather than labelled. Elsewhere on
       the page the same attribute only changes what the card says. */
    var block = document.querySelector('[data-sts-article-products]');
    if (!block) return;

    var shown = 0;
    var inBlock = block.querySelectorAll('[data-sts-approved-count]');
    for (var j = 0; j < inBlock.length; j++) {
      var fit = inBlock[j].getAttribute('data-sts-fit');
      var hide = fit === 'barred' || fit === 'subtype';
      inBlock[j].hidden = hide;
      if (!hide) shown++;
    }

    var empty = block.querySelector('[data-sts-article-products-empty]');
    if (empty) empty.hidden = shown > 0;
  }

  // ------------------------------------------------------- 3. copy link

  function wireCopyLink() {
    var button = document.querySelector('[data-sts-article-copy]');
    if (!button || !navigator.clipboard) return;

    var original = button.textContent;
    button.addEventListener('click', function () {
      navigator.clipboard.writeText(window.location.href).then(function () {
        button.textContent = 'Link copied';
        window.setTimeout(function () {
          button.textContent = original;
        }, 2000);
      }, function () {
        /* Clipboard access can be refused outright. The three share links next
           to this button still work, so failing quietly is the right answer. */
      });
    });
  }

  // ------------------------------------------- 4. sliders, in a safe order

  function injectScript(url) {
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = url;
      script.onload = resolve;
      script.onerror = function () {
        reject(new Error('Failed to load ' + url));
      };
      document.head.appendChild(script);
    });
  }

  function loadSliders() {
    var roots = document.querySelectorAll('.article__product-slider--root');
    if (!roots.length) return;

    var articleJs = attr('data-article-js');
    if (!articleJs) return;

    var swiperCss = attr('data-swiper-css');
    if (swiperCss && !window.Swiper) {
      var stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = swiperCss;
      document.head.appendChild(stylesheet);
    }

    var swiperReady = window.Swiper
      ? Promise.resolve()
      : injectScript(attr('data-swiper-js'));

    swiperReady.then(
      function () {
        return injectScript(articleJs);
      },
      function (error) {
        /* Without Swiper every slider throws inside renderSliders and aborts the
           loop. Remove the empty roots so the article reads cleanly rather than
           showing four loading skeletons that will never resolve. */
        for (var i = 0; i < roots.length; i++) {
          if (roots[i].parentNode) roots[i].parentNode.removeChild(roots[i]);
        }
        console.error('[sts-article] ' + error.message);
      }
    ).catch(function (error) {
      console.error('[sts-article] ' + error.message);
    });
  }

  // ------------------------------------ analytics: call-to-action taps

  /* The call to action draws one link per session state (snippets/
     sts-article-cta.liquid) and says which in data-sts-article-cta-link, so the
     event is named from that rather than guessed: qminus starts the quiz,
     qpartial resumes it, qstale retakes it. qplus goes to the routine, not the
     quiz, and is not one of these events. Delegated, so a CTA that
     article.js renders later from a [[ctaquiz]] shortcode is covered too. */
  var CTA_KIND = { qminus: 'start', qpartial: 'resume', qstale: 'retake' };

  function wireCtaTracking() {
    document.addEventListener('click', function (ev) {
      var link = ev.target && ev.target.closest && ev.target.closest('[data-sts-article-cta-link]');
      if (!link) return;
      var kind = CTA_KIND[link.getAttribute('data-sts-article-cta-link')];
      var T = window.STSTrack;
      if (!kind || !T || typeof T.quizClick !== 'function') return;
      try { T.quizClick('article_cta', link.getAttribute('href'), kind); } catch (e) { /* never break a page */ }
    });
  }

  // ------------------------------------------------------------- boot

  openTableOfContents();
  applyFit();
  wireCopyLink();
  wireCtaTracking();
  loadSliders();

  /* Workstream 02 re-resolves the session when the customer id on the device
     changes — a sign-in, a sign-out, the preview simulator. The verdict on
     every card has to follow it. */
  document.addEventListener('sts:state', applyFit);
}());
